// /* eslint-disable max-statements */
import { Stack, Text } from '@mantine/core';
import moment from 'moment';
import { useMemo } from 'react';

import { TSDData } from '@e-origin/contracts';
import { EORIGIN_DATE_FORMAT_WITH_TIME, TSDWriteOffNotificationStage } from '@e-origin/shared';

import { Table, TableColumnProps } from '@components';

interface WriteOffParsedData {
  stage: string;
  source: string;
  date: string;
  remarks: string;
  grossMassWrittenOff: number;
  numberOfPackagesWrittenOff: string;
}

interface IWriteOffTableProps {
  tsd: TSDData;
}

const checkNotificationStage = (stage: string): string => {
  if (stage === TSDWriteOffNotificationStage.INTERMEDIATE) {
    return 'Intermediate - 60/70/80 days';
  } else if (stage === TSDWriteOffNotificationStage.PROVISIONAL) {
    return 'Provisional - All cleared';
  } else if (stage === TSDWriteOffNotificationStage.FINAL) {
    return 'Final - 90 days';
  }
  return stage;
};

export const WriteOffTable: React.FC<IWriteOffTableProps> = ({ tsd }) => {
  const writeOffMessages = useMemo(() => {
    if (!tsd.customsState.writeOff) return [];

    return tsd.customsState.writeOff
      .map(
        (wo): WriteOffParsedData => ({
          stage: checkNotificationStage(wo.notificationStage),
          source: wo.sourceCodes?.join(', ') || '',
          date: moment(wo.notificationDate).format(EORIGIN_DATE_FORMAT_WITH_TIME),
          remarks: wo.remarks || '',
          grossMassWrittenOff: parseFloat(wo.grossMassWrittenOff) || 0,
          numberOfPackagesWrittenOff: wo.numberOfPackagesWrittenOff || '',
        }),
      )
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [tsd]);

  const columns: TableColumnProps<WriteOffParsedData>[] = [
    {
      name: 'Stage',
      cell: (row) => <Text>{row.stage}</Text>,
      width: '240px',
    },
    {
      name: 'Source',
      cell: (row) => <Text>{row.source}</Text>,
      width: '100px',
    },
    {
      name: 'Date',
      cell: (row) => <Text>{row.date}</Text>,
      width: '180px',
    },
    {
      name: 'Mass',
      cell: (row) => <Text>{row.grossMassWrittenOff}</Text>,
      width: '120px',
    },
    {
      name: 'Packages',
      cell: (row) => <Text>{row.numberOfPackagesWrittenOff}</Text>,
      width: '120px',
    },
    {
      name: 'Remarks',
      cell: (row) => <Text lineClamp={3}>{row.remarks}</Text>,
      width: '400px',
    },
  ];

  if (!writeOffMessages.length) {
    return (
      <Stack align="center">
        <Text>No write-off messages received</Text>
      </Stack>
    );
  }

  return (
    <Stack>
      <Text fw={700}>Write-off Message History</Text>
      <Table<WriteOffParsedData> dataSource={writeOffMessages} columns={columns} isLoading={false} pagination={false} />
    </Stack>
  );
};
